import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import {
  FormsModule,
  ReactiveFormsModule,
  Validators,
  FormControl,
  FormGroup,
} from '@angular/forms';
import { AccountService } from '../account-service.service';
import { Router } from '@angular/router';
import { ChangeDetectorRef } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { UserAccount } from '../user-account';
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from '../app.component';
import { provideHttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';

bootstrapApplication(AppComponent, {
  providers: [provideHttpClient()],
}).catch((err) => console.error(err));

@Component({
  selector: 'app-sign-up-page',
  imports: [
    RouterModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  providers: [AccountService],
  standalone: true,
  templateUrl: './sign-up-page.component.html',
  styleUrls: ['./sign-up-page.component.css'],
})
export class SignUpPageComponent {
  userAccount!: UserAccount;

  constructor(
    private createAccountService: AccountService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
  ) {
    this.userAccount = {
      id: '',
      unique_id: '',
      email: '',
      name: '',
      last_name: '',
      latin_name: '',
      latin_lastname: '',
      birthdate: '',
      country: '',
      city: '',
      mobile_number: '',
      nationality: '',
      gender: 0,
      is_activated: false,
      is_premium: false,
      activation_code: 0,
      is_verified: false,
      is_mobileVerified: false,
      profile_completed: false,
      last_login: '',
      registration_date: '',
      password: '',
      confirm_password: '',
      authentication: '',
      verificationCode: '',
    };
  }

  protected accountExists: boolean = false;
  protected passwordMismatchError: string = '';
  protected passwordComplexityError: string | null = '';
  emailError: string = '';
  protected uri: string = 'createAccount';

  isPasswordEnabled: boolean = true;
  MOBILE_NUMBER_REGEX = /^(00|\+)[0-9]+$/;

  protected applyForm = new FormGroup({
    id: new FormControl(''),
    unique_id: new FormControl(0),
    email: new FormControl('', [Validators.required, Validators.email]),
    name: new FormControl(''),
    last_name: new FormControl(''),
    latin_name: new FormControl(''),
    latin_lastname: new FormControl(''),
    birthdate: new FormControl(''),
    country: new FormControl(''),
    city: new FormControl(''),
    mobile_number: new FormControl('', [
      Validators.required,
      Validators.pattern(this.MOBILE_NUMBER_REGEX),
    ]),
    nationality: new FormControl(''),
    gender: new FormControl(1),
    is_activated: new FormControl(false),
    is_premium: new FormControl(false),
    activation_code: new FormControl(0),
    is_verified: new FormControl(false),
    profile_completed: new FormControl(false),
    last_login: new FormControl(''),
    registration_date: new FormControl(''),
    password: new FormControl('', Validators.required),
    confirm_password: new FormControl('', Validators.required),
    alertMessage: new FormControl(''),
  });

  onEmailInput() {
    if (this.accountExists) {
      this.accountExists = false;
    }
  }

  private isPasswordComplex(password: string): string | null {
    if (password.length < 8) {
      return this.translate.instant('sign-up-page.PASSWORD_TOO_SHORT');
    }

    const uppercasePattern = /[A-Z]+/;
    if (!password.match(uppercasePattern)) {
      return this.translate.instant('sign-up-page.PASSWORD_UPPERCASE');
    }

    const lowercasePattern = /[a-z]+/;
    if (!password.match(lowercasePattern)) {
      return this.translate.instant('sign-up-page.PASSWORD_LOWERCASE');
    }

    const specialCharactersPattern = /[!@#$%^&*()~_+{}:<>?,.`|\/]/;
    if (!password.match(specialCharactersPattern)) {
      return this.translate.instant('sign-up-page.PASSWORD_SPECIAL_CHAR');
    }

    return null;
  }

  resetAccountExists() {
    this.accountExists = false;
  }

  setEmailTouched() {
    const emailControl = this.applyForm.get('email');
    if (emailControl) {
      emailControl.markAsTouched();
    }
  }

  submit() {
    if (this.applyForm.invalid) {
      console.log(
        this.translate.instant('sign-up-page.FORM_INVALID'),
        this.applyForm.errors,
      );
      this.applyForm.markAllAsTouched();
      return;
    }

    this.userAccount.email = (this.applyForm.value.email ?? '').toLowerCase();
    this.userAccount.mobile_number = this.applyForm.value.mobile_number ?? '';

    const complexityError = this.isPasswordComplex(
      this.applyForm.value.password ?? '',
    );
    if (complexityError) {
      this.applyForm.setErrors({ passwordComplexity: true });
      this.passwordComplexityError = complexityError;
      return;
    }

    this.userAccount.password = this.hashPassword(
      this.applyForm.value.password ?? '',
    );
    this.userAccount.confirm_password = this.hashPassword(
      this.applyForm.value.confirm_password ?? '',
    );

    if (this.userAccount.password !== this.userAccount.confirm_password) {
      this.applyForm.setErrors({ passwordMismatch: true });
      this.passwordMismatchError = this.translate.instant(
        'sign-up-page.PASSWORD_MISMATCH',
      );
      return;
    }

    this.createAccountService.run(this.userAccount, this.uri).subscribe({
      next: (response) => {
        console.log(
          this.translate.instant('sign-up-page.CREATE_ACCOUNT_SUCCESS'),
          response,
        );
        this.router.navigate(['login']);
      },
      error: (error) => {
        if (error.status === 409 && error.statusText === 'used_Email') {
          console.error(
            this.translate.instant('sign-up-page.EMAIL_EXISTS'),
            error,
          );
          this.accountExists = true;
          this.cdr.detectChanges();
        } else {
          console.error(
            this.translate.instant('sign-up-page.REQUEST_FAILED'),
            error,
          );
        }
      },
    });
  }

  hashPassword(password: string): string {
    return CryptoJS.SHA256(password).toString(CryptoJS.enc.Hex);
  }
}
